import { Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../ui/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/components/ui/dialog";
import { alsDeutschesDatum, alsZeitpunkt } from "../../feature-hints/datum";
import { Neuigkeit, NeuigkeitStand, STAND_LABEL, STAND_PLAKETTE, useAdminNeuigkeiten } from "./useAdminNeuigkeiten";

/** Tag und Uhrzeit eines geplanten Eintrags — die Uhrzeit ist hier die eigentliche Auskunft. */
/** Die Reiter ueber der Liste: beide Staende einzeln, davor die ungefilterte Ansicht. */
type Reiter = "alle" | NeuigkeitStand;

/**
 * Die Datumsspalte heisst je Reiter anders: Was veroeffentlicht ist, erscheint nicht mehr
 * — es ist erschienen. Eine Ueberschrift fuer drei Staende waere in zwei Faellen falsch.
 */
const DATUM_SPALTE: Record<Reiter, string> = {
  alle: "Datum",
  entwurf: "Datum",
  geplant: "Erscheint",
  veroeffentlicht: "Erschienen",
};

/**
 * Die Liste der Neuigkeiten in der Verwaltung (KAN-276, A6).
 *
 * Es gibt drei Staende: Entwurf, geplant und veroeffentlicht. Die Einordnung (neue Funktion,
 * verbessert, behoben) steht bewusst nicht in der Liste, sondern nur im Formular — zwei
 * Plaketten nebeneinander lesen sich wie zwei konkurrierende Staende.
 */
export const NeuigkeitenListe = () => {
  const navigate = useNavigate();
  const { neuigkeiten, laedt, fehler, laden, loeschen } = useAdminNeuigkeiten();
  const [reiter, setReiter] = useState<Reiter>("alle");
  const [zuLoeschen, setZuLoeschen] = useState<Neuigkeit | null>(null);
  const [loeschfehler, setLoeschfehler] = useState(false);

  const anzahl = (stand: NeuigkeitStand): number => neuigkeiten.filter(eintrag => eintrag.status === stand).length;

  const sichtbar = useMemo(
    () => (reiter === "alle" ? neuigkeiten : neuigkeiten.filter(eintrag => eintrag.status === reiter)),
    [neuigkeiten, reiter]
  );

  const bestaetigtLoeschen = async () => {
    if (!zuLoeschen) return;
    const geklappt = await loeschen(zuLoeschen.id);
    // Scheitert das Loeschen, bleibt der Entwurf vollstaendig erhalten — dann sagt es das
    // Fenster, statt still zuzugehen und so zu tun, als waere es weg.
    if (!geklappt) {
      setLoeschfehler(true);
      return;
    }
    setLoeschfehler(false);
    setZuLoeschen(null);
  };

  const reiterKnopf = (wert: Reiter, label: string, anzahl: number) => {
    const aktiv = reiter === wert;

    return (
      <button
        key={wert}
        type="button"
        role="tab"
        aria-selected={aktiv}
        onClick={() => setReiter(wert)}
        className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-0.5 pb-2 text-sm font-medium ${
          aktiv ? "border-neuigkeit text-neuigkeit-dunkel" : "border-transparent text-gray-500"
        }`}
      >
        {label}
        <span className="font-normal opacity-60">{anzahl}</span>
      </button>
    );
  };

  return (
    <>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3.5">
        <div>
          <h1 className="mb-1 text-[23px] font-bold tracking-tight text-gray-900">Neuigkeiten</h1>
          <p className="text-sm text-gray-600">Veröffentlichtes erscheint bei allen Betrieben. Entwürfe sieht nur ihr.</p>
        </div>
        <Button size="kompakt" onClick={() => navigate("/super-admin/neuigkeiten/neu")} className="text-white">
          Neue Neuigkeit
        </Button>
      </div>

      <div role="tablist" aria-label="Nach Stand filtern" className="mb-4 flex gap-5 border-b border-gray-200">
        {reiterKnopf("alle", "Alle", neuigkeiten.length)}
        {reiterKnopf("entwurf", "Entwürfe", anzahl("entwurf"))}
        {reiterKnopf("geplant", "Geplant", anzahl("geplant"))}
        {reiterKnopf("veroeffentlicht", "Veröffentlicht", anzahl("veroeffentlicht"))}
      </div>

      {laedt && <p className="text-sm text-gray-500">Wird geladen …</p>}

      {!laedt && fehler && (
        // Nie eine leere Flaeche, die wie "nichts vorhanden" aussieht (A6).
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-1 text-base font-bold text-gray-900">Die Liste lässt sich gerade nicht laden</h2>
          <Button size="kompakt" variant="outline" onClick={() => laden()}>
            Noch einmal versuchen
          </Button>
        </div>
      )}

      {!laedt && !fehler && neuigkeiten.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-8 py-14 text-center">
          <h2 className="text-lg font-bold text-gray-900">Hier ist noch nichts</h2>
          <p className="max-w-[46ch] text-sm leading-relaxed text-gray-600">
            Sobald ihr die erste Neuigkeit veröffentlicht, sehen eure Kunden sie in ihrem Neuigkeiten-Bereich.
          </p>
          <Button size="kompakt" onClick={() => navigate("/super-admin/neuigkeiten/neu")} className="mt-2 text-white">
            Erste Neuigkeit schreiben
          </Button>
        </div>
      )}

      {!laedt && !fehler && neuigkeiten.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="grid h-9 grid-cols-[minmax(0,3fr)_150px_minmax(0,1fr)_90px_minmax(0,1fr)_120px_56px] items-center gap-4 border-b border-gray-200 bg-gray-50 px-4 text-xs font-medium uppercase tracking-wide text-gray-500">
            <div>Titel</div>
            <div>Stand</div>
            <div>{DATUM_SPALTE[reiter]}</div>
            <div>Dialog</div>
            <div>Von</div>
            <div>Gelesen</div>
            <div />
          </div>

          {/* Genau so hoch wie eine Zeile: sonst springt die Tabelle beim Reiterwechsel. */}
          {sichtbar.length === 0 && (
            <p className="flex min-h-12 items-center px-4 text-sm text-gray-500">In diesem Reiter steht nichts.</p>
          )}

          {sichtbar.map(eintrag => (
            <div
              key={eintrag.id}
              className="grid min-h-12 grid-cols-[minmax(0,3fr)_150px_minmax(0,1fr)_90px_minmax(0,1fr)_120px_56px] items-center gap-4 border-b border-gray-100 px-4 py-2 last:border-b-0"
            >
              <Link to={`/super-admin/neuigkeiten/${eintrag.id}`} className="min-w-0 truncate text-sm font-medium text-gray-900">
                {eintrag.title || "Ohne Titel"}
              </Link>
              <div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STAND_PLAKETTE[eintrag.status]}`}>
                  {STAND_LABEL[eintrag.status]}
                </span>
              </div>
              <div className="text-sm text-gray-700">
                {eintrag.status === "geplant" && eintrag.publishAt ? alsZeitpunkt(eintrag.publishAt) : alsDeutschesDatum(eintrag.date)}
              </div>
              <div className="text-[13px] text-gray-700">{eintrag.opensDialog ? "Ja" : "Nein"}</div>
              {/* Wer den Eintrag geschrieben hat (KAN-297). Die Erstbefuellung hat niemanden. */}
              <div className="min-w-0 truncate text-[13px] text-gray-600">{eintrag.author ?? "kandidat.io"}</div>
              {/* Nur was zaehlbar ist: Ein Entwurf hat niemanden erreicht, da waere eine Null
                  eine Behauptung (KAN-296). */}
              <div className="text-[13px] text-gray-700">
                {eintrag.status === "veroeffentlicht" && eintrag.zahlen ? (
                  // Derselbe Bezug wie in der Schreibmaske: wie viele von denen, die den
                  // Eintrag haetten sehen koennen. Eine nackte Zahl sagt nichts.
                  <span title={`${eintrag.zahlen.geoeffnet} geöffnet, aus ${eintrag.zahlen.betriebe} Betrieben`}>
                    {eintrag.zahlen.erreicht}
                    {eintrag.zahlen.moeglich > 0 && <span className="text-gray-400"> von {eintrag.zahlen.moeglich}</span>}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
              <div className="flex justify-end">
                {/* Loeschen gibt es nur fuer Entwuerfe. Was veroeffentlicht ist, wird zuerst
                    zurueckgezogen — so entfernt niemand etwas, das beim Kunden auf dem Schirm steht. */}
                {eintrag.status === "entwurf" && (
                  <button
                    type="button"
                    aria-label={`Entwurf „${eintrag.title || "Ohne Titel"}" löschen`}
                    onClick={() => {
                      setLoeschfehler(false);
                      setZuLoeschen(eintrag);
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
                  >
                    <Trash2 className="h-[18px] w-[18px]" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={zuLoeschen !== null}
        onOpenChange={offen => {
          if (!offen) {
            setZuLoeschen(null);
            setLoeschfehler(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Diesen Entwurf löschen?</DialogTitle>
            <DialogDescription>
              „{zuLoeschen?.title || "Ohne Titel"}" wird endgültig entfernt. Der Entwurf war bei keinem Kunden sichtbar, es geht also
              nichts verloren, was jemand schon gelesen hat. Rückgängig machen lässt sich das nicht.
            </DialogDescription>
          </DialogHeader>
          {loeschfehler && (
            <p className="text-sm text-red-600">Das Löschen hat nicht geklappt. Der Entwurf ist unverändert erhalten.</p>
          )}
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button size="kompakt" variant="outline" onClick={() => setZuLoeschen(null)}>
              Abbrechen
            </Button>
            <Button size="kompakt" onClick={bestaetigtLoeschen} className="bg-red-700 text-white hover:bg-red-800">
              Entwurf löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NeuigkeitenListe;
